import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { ArrowLeft } from 'lucide-react';

const MasseNettoChart = ({ data, onBack }) => {
  const [animationKey, setAnimationKey] = useState(Date.now());
  const [isMounted, setIsMounted] = useState(false);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [showDailySum, setShowDailySum] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dailyDetailData, setDailyDetailData] = useState([]);

  // First render handling - ensures animation runs smoothly on initial render
  useEffect(() => {
    // Mark component as mounted
    setIsMounted(true);
    // Set a slight delay before starting the initial animation
    const timer = setTimeout(() => {
      setAnimationKey(Date.now());
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Helper function to calculate Y-axis maximum with proper spacing
  const calculateYAxisMax = (data, isDaily = false) => {
    if (!data || data.length === 0) return 'auto';

    let maxValue = 0;

    if (isDaily) {
      // For daily detail data, find max value
      maxValue = Math.max(...data.map(item => item.value || 0));
    } else if (showDailySum) {
      // For stacked bar chart, find max total value
      maxValue = Math.max(...data.map(item => item.totalValue || 0));
    } else {
      // For line chart, find max individual value
      maxValue = Math.max(...data.map(item => item.value || 0));
    }

    // Add 15% padding to the maximum value for better visual spacing
    return Math.ceil(maxValue * 1.15);
  };

  // Process data when it changes or aggregation mode changes
  useEffect(() => {
    if (data) {
      // Reset selected day when toggling between views
      setSelectedDay(null);

      const processedData = prepareTimeSeriesData(data, showDailySum);
      setTimeSeriesData(processedData);
      // Reset animation key to trigger smooth animation on mode change
      setAnimationKey(Date.now());
    }
  }, [data, showDailySum]);

  // Handle bar click to show daily detail
  const handleBarClick = (data) => {
    if (!data || !data.originalDate) return;

    // Get the date without time
    const clickedDate = new Date(data.originalDate);
    const dayStart = new Date(
      clickedDate.getFullYear(),
      clickedDate.getMonth(),
      clickedDate.getDate()
    );

    // Get the next day
    const dayEnd = new Date(
      clickedDate.getFullYear(),
      clickedDate.getMonth(),
      clickedDate.getDate() + 1
    );

    // Format the date for display
    const formattedDate = formatDate(clickedDate);
    setSelectedDay(formattedDate);

    // Filter data for this day only
    const dayData = prepareDetailedDayData(data.originalDate);
    setDailyDetailData(dayData);

    // Reset animation key for smooth animation
    setAnimationKey(Date.now());
  };

  // Function to prepare detailed data for a specific day
  const prepareDetailedDayData = (dayDate) => {
    const pages = data.pages || [];
    const targetDate = new Date(dayDate);
    const dayStart = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );
    const dayEnd = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate() + 1
    );

    // Filter and map data for the selected day
    const dayData = pages
      .map(item => {
        let timestamp = null;

        // First try to parse from Zeitstempel
        if (item.Zeitstempel) {
          const parts = item.Zeitstempel.split(' ');
          if (parts.length === 2) {
            const dateParts = parts[0].split('.');
            const timeParts = parts[1].split(':');

            if (dateParts.length === 3 && timeParts.length >= 2) {
              timestamp = new Date(
                parseInt(dateParts[2]), // Year
                parseInt(dateParts[1]) - 1, // Month (0-based)
                parseInt(dateParts[0]), // Day
                parseInt(timeParts[0]), // Hour
                parseInt(timeParts[1])  // Minute
              );
            }
          }
        }

        // If Zeitstempel parsing failed, try the original ISO timestamp
        if (!timestamp && item.originalData?.standard?.documentInfo?.timeStamp) {
          timestamp = new Date(item.originalData.standard.documentInfo.timeStamp);
        }

        const masseNetto = parseFloat(item['Masse Netto'] ||
          (item.originalData?.standard?.material?.quantities?.netto?.value || 0));

        // Get material name
        const material = getMaterialName(item);

        return {
          date: timestamp,
          value: masseNetto,
          material: material
        };
      })
      .filter(item => item.date && item.date >= dayStart && item.date < dayEnd) // Filter for selected day
      .sort((a, b) => a.date - b.date); // Sort chronologically

    // Format dates for display
    return dayData.map(item => ({
      date: formatTime(item.date), // Only show time for detailed day view
      value: item.value,
      material: item.material,
      // Store original date for tooltip
      originalDate: item.date
    }));
  };

  // Format time only (for detailed day view)
  const formatTime = (date) => {
    if (!date) return '';

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
  };

  // Function to get material name from item
  const getMaterialName = (item) => {
    // Try to get material from the flattened data
    let material = item.Material;

    // If not available, try to get from original data
    if (!material && item.originalData?.standard?.material?.asphaltMixture) {
      material = item.originalData.standard.material.asphaltMixture;
    }

    // If still not available, use a default value
    if (!material) {
      material = "Unbekannt";
    }

    return material;
  };

  // Function to generate a consistent color for a material
  const getMaterialColor = (material, index) => {
    // Predefined color palette for better visual distinction
    const colorPalette = [
      '#22c55e', // Green (primary color)
      '#3b82f6', // Blue
      '#f97316', // Orange
      '#8b5cf6', // Purple
      '#ec4899', // Pink
      '#14b8a6', // Teal
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#64748b', // Slate
      '#84cc16'  // Lime
    ];

    // Use index if provided, otherwise use a hash of the material name
    if (typeof index === 'number') {
      return colorPalette[index % colorPalette.length];
    }

    // Simple hash function to get a consistent index for a material name
    let hash = 0;
    for (let i = 0; i < material.length; i++) {
      hash = material.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use the absolute value of the hash to get a positive index
    const colorIndex = Math.abs(hash) % colorPalette.length;
    return colorPalette[colorIndex];
  };

  // Function to prepare time series data
  const prepareTimeSeriesData = (data, aggregateByDay = false) => {
    // Extract pages data from the file
    const pages = data.pages || [];

    // Map and sort by timestamp
    const mappedData = pages
      .map(item => {
        let timestamp = null;

        // First try to parse from Zeitstempel (which is already formatted as DD.MM.YYYY HH:MM)
        if (item.Zeitstempel) {
          // Parse German format date (DD.MM.YYYY HH:MM)
          const parts = item.Zeitstempel.split(' ');
          if (parts.length === 2) {
            const dateParts = parts[0].split('.');
            const timeParts = parts[1].split(':');

            if (dateParts.length === 3 && timeParts.length >= 2) {
              // Create date in format: YYYY-MM-DDTHH:MM
              timestamp = new Date(
                parseInt(dateParts[2]), // Year
                parseInt(dateParts[1]) - 1, // Month (0-based)
                parseInt(dateParts[0]), // Day
                parseInt(timeParts[0]), // Hour
                parseInt(timeParts[1])  // Minute
              );
            }
          }
        }

        // If Zeitstempel parsing failed, try the original ISO timestamp
        if (!timestamp && item.originalData?.standard?.documentInfo?.timeStamp) {
          timestamp = new Date(item.originalData.standard.documentInfo.timeStamp);
        }

        const masseNetto = parseFloat(item['Masse Netto'] ||
          (item.originalData?.standard?.material?.quantities?.netto?.value || 0));

        // Get material name
        const material = getMaterialName(item);

        return {
          date: timestamp,
          value: masseNetto,
          material: material
        };
      })
      .filter(item => item.date) // Remove items without valid dates
      .sort((a, b) => a.date - b.date); // Sort chronologically

    // If aggregation by day is enabled, group and sum by day and material
    let processedData = mappedData;

    if (aggregateByDay && mappedData.length > 0) {
      // Create a map to store daily sums by material
      const dailySumsByMaterial = new Map();

      // Keep track of all unique materials
      const allMaterials = new Set();

      // Group by day and material, then sum values
      mappedData.forEach(item => {
        // Create a date string with just the date part (no time)
        const dateObj = item.date;
        const dateKey = `${dateObj.getFullYear()}-${dateObj.getMonth()}-${dateObj.getDate()}`;
        const material = item.material;

        // Add to set of all materials
        allMaterials.add(material);

        // Create a compound key for date + material
        const compoundKey = `${dateKey}|${material}`;

        // Initialize entry if it doesn't exist
        if (!dailySumsByMaterial.has(compoundKey)) {
          dailySumsByMaterial.set(compoundKey, {
            date: new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()),
            material: material,
            value: 0,
            count: 0
          });
        }

        // Add to the sum and increment count
        const dayMaterialData = dailySumsByMaterial.get(compoundKey);
        dayMaterialData.value += item.value;
        dayMaterialData.count += 1;
      });

      // Convert map to array
      const materialDataArray = Array.from(dailySumsByMaterial.values());

      // Group by date for stacked bar chart format
      const dateGroups = new Map();

      materialDataArray.forEach(item => {
        const dateStr = formatDate(item.date);

        if (!dateGroups.has(dateStr)) {
          dateGroups.set(dateStr, {
            date: dateStr,
            originalDate: item.date,
            totalValue: 0,
            totalCount: 0,
            // Store material data in a separate object to avoid conflicts with date and other properties
            materials: {}
          });
        }

        const dateGroup = dateGroups.get(dateStr);
        dateGroup.materials[item.material] = item.value;
        dateGroup.totalValue += item.value;
        dateGroup.totalCount += item.count;
      });

      // Convert to array and sort by date
      processedData = Array.from(dateGroups.values());
      processedData.sort((a, b) => new Date(a.originalDate) - new Date(b.originalDate));

      // Add all materials to each data point (with 0 if not present)
      // This ensures the stacked bar chart works correctly
      const materialsArray = Array.from(allMaterials);

      processedData.forEach(item => {
        materialsArray.forEach(material => {
          if (!item.materials[material]) {
            item.materials[material] = 0;
          }
        });

        // Add material colors
        item.materialColors = {};
        materialsArray.forEach((material, index) => {
          item.materialColors[material] = getMaterialColor(material, index);
        });
      });

      // Store the list of all materials for later use
      processedData.allMaterials = materialsArray;
    }

    // For non-aggregated data, just format dates
    if (!aggregateByDay) {
      return processedData.map(item => ({
        date: formatDate(item.date),
        value: item.value,
        material: item.material,
        // Store original date for tooltip
        originalDate: item.date
      }));
    }

    return processedData;
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';

    // If it's already a string, try to parse it
    if (typeof date === 'string') {
      date = new Date(date);
    }

    // Explicitly format as DD.MM.YYYY to ensure consistency
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  // Format date for tooltip
  const formatTooltipDate = (date) => {
    if (!date) return '';

    // Ensure date is a Date object
    const dateObj = new Date(date);

    // Explicitly format as DD.MM.YYYY HH:MM to ensure consistency
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  return (
    <div className="flex flex-col" style={{
      height: '75vh',
      minHeight: '50vh',
      maxHeight: '80vh'
    }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          {selectedDay ? (
            <button
              onClick={() => setSelectedDay(null)}
              className="flex items-center text-blue-500 hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück zur Übersicht
            </button>
          ) : (
            <button
              onClick={onBack}
              className="flex items-center text-blue-500 hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück zur Tabelle
            </button>
          )}
          <h3 className="text-lg font-semibold ml-4">
            {selectedDay
              ? `MASSE NETTO am ${selectedDay}`
              : 'MASSE NETTO über Zeit'}
          </h3>
        </div>

        {!selectedDay && (
          <div className="flex items-center">
            <span className={`mr-2 ${!showDailySum ? 'font-medium' : 'text-gray-500'}`}>Einzelwerte</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                value=""
                className="sr-only peer"
                checked={showDailySum}
                onChange={() => setShowDailySum(!showDailySum)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
            <span className={`ml-2 ${showDailySum ? 'font-medium' : 'text-gray-500'}`}>Tagessummen</span>
          </div>
        )}
      </div>

      <div style={{ height: '65vh' }}>
        {selectedDay ? (
          // Daily detail view (scatter plot with colored dots for a specific day)
          dailyDetailData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                key={`daily-detail-${animationKey}`}
                margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  height={40}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  label={{
                    value: 'MASSE NETTO (t)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -5
                  }}
                  domain={[0, calculateYAxisMax(dailyDetailData, true)]}
                />
                <Tooltip
                  formatter={(value, name, props) => {
                    return [`${value.toFixed(2)} t`, name];
                  }}
                  labelFormatter={(label, payload) => {
                    // Use the original date for a more detailed tooltip
                    if (payload && payload.length > 0 && payload[0]?.payload?.originalDate) {
                      return formatTooltipDate(payload[0]?.payload?.originalDate);
                    }
                    return label;
                  }}
                />
                <Legend />
                {/* Create a line for each material - but display only as dots without connecting lines */}
                {(() => {
                  // Get unique materials and times
                  const uniqueMaterials = [...new Set(dailyDetailData.map(item => item.material))];
                  const uniqueTimes = [...new Set(dailyDetailData.map(item => item.date))].sort();

                  // Create a line for each material
                  return uniqueMaterials.map((material, index) => {
                    // Create a data point for each time, with null values where this material doesn't have data
                    const materialData = uniqueTimes.map(time => {
                      const dataPoint = dailyDetailData.find(item =>
                        item.date === time && item.material === material
                      );

                      return dataPoint || {
                        date: time,
                        value: null,
                        material: material,
                        originalDate: null
                      };
                    });

                    return (
                      <Line
                        key={material}
                        type="monotone"
                        dataKey="value"
                        data={materialData}
                        stroke={getMaterialColor(material, index)}
                        strokeWidth={0}
                        strokeOpacity={0}
                        name={material}
                        dot={{
                          r: 6,
                          fill: getMaterialColor(material, index),
                          stroke: getMaterialColor(material, index),
                          strokeWidth: 2
                        }}
                        isAnimationActive={false}
                        connectNulls={false}
                      />
                    );
                  });
                })()}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <p>Keine Daten verfügbar für diesen Tag</p>
            </div>
          )
        ) : timeSeriesData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            {!showDailySum ? (
              // Line chart for individual values
              <LineChart
                key={`massenetto-line-${animationKey}`}
                data={timeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  label={{
                    value: 'MASSE NETTO (t)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -5
                  }}
                  domain={[0, calculateYAxisMax(timeSeriesData)]}
                />
                <Tooltip
                  formatter={(value, name, props) => {
                    const material = props?.payload?.material || 'MASSE NETTO';
                    return [`${value.toFixed(2)} t`, material];
                  }}
                  labelFormatter={(label, payload) => {
                    // Use the original date for a more detailed tooltip
                    if (payload && payload.length > 0 && payload[0]?.payload?.originalDate) {
                      return formatTooltipDate(payload[0]?.payload?.originalDate);
                    }
                    return label;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#22c55e"
                  name="MASSE NETTO"
                  strokeWidth={2.5}
                  dot={false}
                  isAnimationActive={true}
                  animationDuration={1500}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            ) : (
              // Stacked Bar chart for daily sums by material
              <BarChart
                key={`massenetto-bar-${animationKey}`}
                data={timeSeriesData}
                margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                onClick={(data) => data && data.activePayload && data.activePayload.length > 0 && handleBarClick(data.activePayload[0].payload)}
                stackOffset="normal"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  label={{
                    value: 'MASSE NETTO (t)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -5
                  }}
                  domain={[0, calculateYAxisMax(timeSeriesData)]}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      // Filter out materials with 0 value
                      const nonZeroPayloads = payload.filter(p => p.value > 0);

                      // If no non-zero values, don't show tooltip
                      if (nonZeroPayloads.length === 0) return null;

                      // Format date for display
                      const dateLabel = payload[0]?.payload?.originalDate
                        ? formatDate(payload[0]?.payload?.originalDate)
                        : label;

                      return (
                        <div className="bg-white p-2 border border-gray-200 shadow-md rounded-md">
                          <p className="font-medium text-gray-700">{dateLabel}</p>
                          <div className="mt-2">
                            {nonZeroPayloads.map((entry, index) => (
                              <div key={`item-${index}`} className="flex items-center mb-1">
                                <div
                                  className="w-3 h-3 mr-2 rounded-sm"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <p className="text-sm">
                                  <span className="font-medium">{entry.name}: </span>
                                  <span>{entry.value.toFixed(2)} t</span>
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                {/* Create a Bar for each material */}
                {timeSeriesData.allMaterials && timeSeriesData.allMaterials.map((material, index) => (
                  <Bar
                    key={material}
                    dataKey={`materials.${material}`}
                    name={material}
                    stackId="a"
                    fill={getMaterialColor(material, index)}
                    radius={index === timeSeriesData.allMaterials.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    isAnimationActive={true}
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                    cursor="pointer" // Show pointer cursor on hover
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <p>Keine Daten verfügbar für die Zeitreihenanalyse</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MasseNettoChart;
